import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {from, Observable, of, switchMap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';

/**
 * Lightweight client for public analytics beacons (browse impressions).
 * Session-dedupes item IDs so the same listing isn't re-sent repeatedly.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsBeaconService {
  private readonly baseUrlPromise: Promise<string>;
  private readonly sessionKey = 'analytics_impressed_ids';
  private impressed = new Set<string>();

  constructor(private http: HttpClient) {
    this.baseUrlPromise = environment.then((env: any) => env.backendURL + '/api/analytics');
    this.loadSession();
  }

  recordImpressions(itemIds: Array<string | null | undefined>): void {
    const fresh = Array.from(new Set(
      (itemIds || [])
        .map(id => (id == null ? '' : String(id).trim()))
        .filter(id => id.length > 0 && !this.impressed.has(id))
    ));
    if (!fresh.length) {
      return;
    }
    fresh.forEach(id => this.impressed.add(id));
    this.persistSession();
    const chunkSize = 50;
    for (let i = 0; i < fresh.length; i += chunkSize) {
      this.postImpressions(fresh.slice(i, i + chunkSize)).subscribe({error: () => {/* ignore beacon failures */}});
    }
  }

  private postImpressions(itemIds: string[]): Observable<unknown> {
    return from(this.baseUrlPromise).pipe(
      switchMap(baseUrl => this.http.post(`${baseUrl}/impressions`, {itemIds})),
      catchError(() => of(null))
    );
  }

  private loadSession(): void {
    try {
      const raw = sessionStorage.getItem(this.sessionKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((id: unknown) => {
          if (typeof id === 'string' && id) {
            this.impressed.add(id);
          }
        });
      }
    } catch {
      // ignore
    }
  }

  private persistSession(): void {
    try {
      // Cap growth so sessionStorage stays small.
      const ids = Array.from(this.impressed).slice(-500);
      sessionStorage.setItem(this.sessionKey, JSON.stringify(ids));
    } catch {
      // ignore
    }
  }
}
